import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import styled, { css } from 'styled-components';
import { get, throttle } from 'lodash';
import { breakpoint, Breakpoints, getWindowBreakpoint, getWindowWidth } from '@zocdoc/components/src/grid';
import { grey, grey10, grey50, grey75, navy, white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import { TakeoverModal } from '@zocdoc/components/src/TakeoverModal';
import { Button } from '@zocdoc/components/src/button';
import { linkStyle } from '@zocdoc/components/src/link';
import { ModalButtonContainer, ModalHeader } from '@zocdoc/components/src/modal';
import {
    ApplySection,
    ApplyButton,
    ClearButton,
    OptionCount,
} from '../styled-primitives';
import HideBranchJourney from '../../../../../Shared/HideBranchJourney';

export default class FacetDropdown extends React.Component {
    static propTypes = {
        displayName: PropTypes.string,
        isMulti: PropTypes.bool,
        selectionRequired: PropTypes.bool,
        options: PropTypes.array,
        showDropdown: PropTypes.bool,
        checkedOptions: PropTypes.object,
        onApplyFacet: PropTypes.func,
        onChangeCheckbox: PropTypes.func,
        onChangeRadio: PropTypes.func,
        onClickClearButton: PropTypes.func,
    };

    static defaultProps = {
        displayName: '',
        isMulti: false,
        selectionRequired: false,
        options: [],
        showDropdown: false,
    };

    state = {
        cachedWindowBreakpoint: 0,
    };

    scrollableDropdownArea = React.createRef();

    componentDidUpdate(prevProps) {
        if (!prevProps.showDropdown && this.props.showDropdown) {
            const scrollableArea = findDOMNode(this.scrollableDropdownArea.current);
            if (scrollableArea) {
                scrollableArea.scrollTop = 0;
            }
        }
        return null;
    }

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        return (
            <div>
                {this.renderDropdown()}
                {this.maybeRenderModal()}
            </div>
        );
    }

    renderDropdown() {
        const DropdownWrapper = this.props.isMulti ? Dropdown : RadioDropdown;
        return (
            <DropdownWrapper
                data-test="facet-dropdown"
                hidden={!this.props.showDropdown}
            >
                {this.renderOptions()}
                <ApplySection data-test="facet-dropdown-buttons">
                    <ApplyButton
                        data-test="facet-dropdown-apply-button"
                        onClick={this.props.onApplyFacet}
                    >
                        apply
                    </ApplyButton>
                    <ClearButton
                        data-test="facet-dropdown-clear-button"
                        disabled={this.isOptionsClearButtonDisabled()}
                        hidden={this.props.selectionRequired}
                        onClick={this.props.onClickClearButton}
                    >
                        clear
                    </ClearButton>
                </ApplySection>
            </DropdownWrapper>
        );
    }

    maybeRenderModal() {
        if (this.props.showDropdown && this.state.cachedWindowBreakpoint < Breakpoints.small) {
            return (
                <FacetTakeoverModal
                    data-test="facet-modal"
                    onClickCancel={this.props.onApplyFacet}
                >
                    <HideBranchJourney />
                    <ModalContent>
                        <ModalScrollableArea>
                            <StyledModalHeader data-test="facet-modal-title">
                                {this.props.displayName}
                            </StyledModalHeader>
                            {this.renderOptions()}
                        </ModalScrollableArea>
                        <ModalButtonContainer>
                            <ButtonGroup>
                                <Button
                                    data-test="facet-modal-apply-button"
                                    onClick={this.props.onApplyFacet}
                                >
                                    apply
                                </Button>
                                <ModalClearButton
                                    data-test="facet-modal-clear-button"
                                    disabled={this.isOptionsClearButtonDisabled()}
                                    hidden={this.props.selectionRequired}
                                    onClick={this.props.onClickClearButton}
                                >
                                    clear
                                </ModalClearButton>
                            </ButtonGroup>
                        </ModalButtonContainer>
                    </ModalContent>
                </FacetTakeoverModal>
            );
        }
    }

    renderOptions() {
        const OptionDisplay = this.isDesktop() ? DropdownOption : ModalOption;
        const whichDataTest = this.isDesktop() ? 'facet-dropdown-option' : 'facet-modal-option';
        return (
            <Options ref={this.scrollableDropdownArea}>
                {this.props.options.map((option, index) => (
                    <OptionDisplay
                        key={option.optionId}
                        data-test={whichDataTest}
                    >
                        <OptionLabel data-test={`${whichDataTest}-label`}>
                            {this.renderCorrectOptionType(option, index)}
                            {option.displayName}
                        </OptionLabel>

                        {this.maybeRenderOptionCount(option, whichDataTest)}
                    </OptionDisplay>
                ))}
            </Options>
        );
    }

    maybeRenderOptionCount(option, whichDataTest) {
        if (option.count) {
            return (
                <OptionCount data-test={`${whichDataTest}-count`}>
                    {option.count}
                </OptionCount>
            );
        } else {
            return (
                <OptionCount data-test={`${whichDataTest}-count`} />
            );
        }
    }

    renderCorrectOptionType(option, index) {
        const { checkedOptions, isMulti } = this.props;
        const inputType = isMulti ? 'checkbox' : 'radio';
        const onChangeOption = isMulti ? this.props.onChangeCheckbox : this.props.onChangeRadio;
        const whichDataTest = isMulti ? 'facet-checkbox' : 'facet-radio';
        return (
            <StyledInput
                checked={checkedOptions.has(option.optionId)}
                data-option-index={index}
                data-test={whichDataTest}
                onChange={onChangeOption}
                type={inputType}
            />
        );
    }

    isOptionsClearButtonDisabled() {
        return get(this.props.checkedOptions, 'size') === 0;
    }

    handleResize = throttle(() => {
        const cachedWindowBreakpoint = getWindowBreakpoint();

        if (cachedWindowBreakpoint !== this.state.cachedWindowBreakpoint) {
            this.setState({ cachedWindowBreakpoint });
        }
    }, 500);

    isDesktop() {
        return getWindowWidth() >= Breakpoints.small;
    }
}

const Dropdown = styled.div`
    background-color: ${white};
    border-radius: 2px;
    border: solid 1px ${grey50};
    box-shadow: 0 1px 0 0 ${grey};
    left: 0;
    margin-top: 8px;
    padding-top: 10px;
    position: absolute;
    top: 100%;
    z-index: 110;

    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const RadioDropdown = Dropdown.withComponent('fieldset');

const FacetTakeoverModal = styled(TakeoverModal)`
    -webkit-overflow-scrolling: touch;

    ${breakpoint.small} {
        display: none;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const DropdownOption = styled.label`
    display: flex;
    padding-bottom: 5px;
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 6px;
    white-space: nowrap;

    &:hover {
        background-color: ${grey10};
        cursor: pointer;
    }
`;

const ModalOption = styled.label`
    display: flex;
    padding-bottom: 5px;
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 6px;
    width: 100%;

    &:hover {
        background-color: ${grey10};
        cursor: pointer;
    }
`;

const Options = styled.div`
    overflow: auto;

    ${breakpoint('small', { down: true })} {
        flex: 1 1 auto;
    }

    ${breakpoint.small} {
        max-height: 300px;
    }
`;

const OptionLabel = styled.div`
    color: ${navy};
    font: 15px ${FontFamily.SharpSansMedium};
    margin-right: 16px;
    white-space: nowrap;
    ${breakpoint('medium', { down: true })} {
        margin-right: 5px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const StyledInput = styled.input`
    margin-right: 10px;
`;

const ModalContent = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
`;

const ModalScrollableArea = styled.div`
    flex: 1 1 auto;
    overflow: auto;
`;

const StyledModalHeader = styled(ModalHeader)`
    flex: 0 1 auto;
`;

const ModalClearButton = styled.div`
    ${linkStyle}
    font: 18px/20px ${FontFamily.SharpSansSemibold};

    ${p => p.disabled && css`
        color: ${grey75};
        border: none;

        &:hover {
            border: none;
        }
    `}
`;
